import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  IconButton,
  Tooltip,
  Fab,
} from '@mui/material';
import {
  Science,
  Whatshot,
  LocalDrink,
  ColorLens,
  PlayArrow,
  Delete,
} from '@mui/icons-material';
import { getGas, listGasIds, type GasId } from '../labs/gases/gasDatabase';
import { getReaction } from '../labs/gases/reactions';
import { determineCollectionMethod } from '../labs/gases/solubility';
import { getSafetyWarnings, validateSafety } from '../labs/gases/safety';
import { getTestOutcome, type GasTest } from '../labs/gases/tests';
import PracticalInstructions from './PracticalInstructions';

const TEST_TOOLS: Array<{ key: GasTest; label: string; icon: React.ReactNode }> = [
  { key: 'lit-splint', label: 'Lit splint', icon: <Whatshot /> },
  { key: 'glowing-splint', label: 'Glowing splint', icon: <PlayArrow /> },
  { key: 'limewater', label: 'Limewater', icon: <LocalDrink /> },
  { key: 'damp-red-litmus', label: 'Damp red litmus', icon: <ColorLens /> },
  { key: 'damp-blue-litmus', label: 'Damp blue litmus', icon: <ColorLens /> },
  { key: 'white-fumes', label: 'HCl / ammonia fumes', icon: <Science /> },
  { key: 'dichromate', label: 'Dichromate paper', icon: <ColorLens /> },
  { key: 'lead-acetate', label: 'Lead acetate paper', icon: <Science /> },
  { key: 'indicator', label: 'Gas indicator', icon: <ColorLens /> },
  { key: 'waft-smell', label: 'Wafting test', icon: <Science /> },
];

const GAS_APPEARANCE: Record<GasId, string> = {
  h2: 'rgba(222, 245, 255, 0.55)',
  o2: 'rgba(222, 245, 255, 0.55)',
  co2: 'rgba(222, 245, 255, 0.55)',
  nh3: 'rgba(222, 245, 255, 0.55)',
  cl2: 'rgba(190, 205, 84, 0.72)',
  so2: 'rgba(222, 245, 255, 0.55)',
  hcl: 'rgba(222, 245, 255, 0.55)',
  no2: 'rgba(154, 73, 39, 0.8)',
  n2: 'rgba(222, 245, 255, 0.55)',
  h2s: 'rgba(222, 245, 255, 0.55)',
};

const gasTestLabel = (test: GasTest): string =>
  TEST_TOOLS.find((tool) => tool.key === test)?.label || test;

const randomGasId = (): GasId => {
  const gasIds = listGasIds() as GasId[];
  return gasIds[Math.floor(Math.random() * gasIds.length)];
};

interface GasTestSimulationProps {
  onMeasurementsUpdate?: (measurement: { name: string; value: number; unit: string }) => void;
  onCalculationFeedback?: (feedback: any) => void;
}

interface Observation {
  id: string;
  test: GasTest;
  observation: string;
  inference: string;
  timestamp: Date;
}

interface TestResult {
  gasId: GasId;
  test: GasTest;
  observation: string;
  result: 'positive' | 'negative' | 'warning';
}

const GasTestSimulation: React.FC<GasTestSimulationProps> = ({
  onMeasurementsUpdate,
  onCalculationFeedback,
}) => {
  const [currentGas, setCurrentGas] = useState<GasId | ''>('');
  const [observations, setObservations] = useState<Observation[]>([]);
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [identifiedGas, setIdentifiedGas] = useState<string>('');
  const [reasoning, setReasoning] = useState<string>('');
  const [showResult, setShowResult] = useState<boolean>(false);
  const [animatingTest, setAnimatingTest] = useState<string>('');
  const [safetyMessages, setSafetyMessages] = useState<string[]>([]);

  useEffect(() => {
    setCurrentGas(randomGasId());
  }, []);

  const performTest = (testType: GasTest) => {
    if (!currentGas || !testType) return;

    const outcome = getTestOutcome(currentGas, testType);
    const safetyAction = testType === 'lit-splint' ? 'ignite' : testType;
    const safety = validateSafety(currentGas, safetyAction);
    const warnings = getSafetyWarnings(currentGas, safetyAction);
    setSafetyMessages([...warnings, ...safety.guidance]);

    setAnimatingTest(testType);

    setTimeout(() => {
      const newObservation: Observation = {
        id: `${Date.now()}-${testType}`,
        test: testType,
        observation: outcome.observation,
        inference: '',
        timestamp: new Date(),
      };

      setObservations(prev => [...prev, newObservation]);
      setTestResults(prev => [...prev, { gasId: currentGas, test: testType, observation: outcome.observation, result: outcome.result }]);

      setAnimatingTest('');
    }, 1200);
  };

  const updateObservation = (id: string, field: 'observation' | 'inference', value: string) => {
    setObservations(prev =>
      prev.map(obs => obs.id === id ? { ...obs, [field]: value } : obs)
    );
  };

  const deleteObservation = (id: string) => {
    setObservations(prev => prev.filter(obs => obs.id !== id));
    setTestResults(prev => prev.filter((result) => result.test !== observations.find((item) => item.id === id)?.test));
  };

  const submitResults = () => {
    if (!currentGas || !identifiedGas || testResults.length === 0) return;

    const identificationCorrect = identifiedGas === currentGas;
    const uniqueTests = new Set(testResults.map((result) => result.test)).size;
    const evidenceScore = Math.round((Math.min(uniqueTests, 3) / 3) * 20);
    const identificationScore = identificationCorrect ? 60 : 0;
    const reasoningScore = reasoning.trim() ? 20 : 0;
    const totalScore = identificationScore + evidenceScore + reasoningScore;
    const accuracy = totalScore;

    const feedback = {
      totalScore,
      accuracy: accuracy,
      procedureFollowed: Math.min(100, uniqueTests * 25),
      dataQuality: observations.length ? Math.round((observations.filter((item) => item.inference.trim()).length / observations.length) * 100) : 0,
      reportQuality: reasoning.trim() ? 100 : 40,
      grade: totalScore >= 85 ? 'A' : totalScore >= 70 ? 'B' : totalScore >= 55 ? 'C' : 'D',
      feedback: `${identificationCorrect ? 'Correct' : 'Incorrect'} gas identification. ${uniqueTests} distinct tests performed. ${reasoning.trim() ? 'Reasoning recorded.' : 'Add reasoning to complete the report.'}`,
      results: {
        identifiedGas,
        actualGas: getGas(currentGas).name,
        identificationCorrect,
        testResults,
        observations,
        reasoning,
        generation: getReaction(currentGas),
        collection: determineCollectionMethod(currentGas),
        workingsTable: {
          title: 'Gas Test Observations',
          columns: ['Test Performed', 'Observation', 'Inference'],
          rows: observations.map(obs => [gasTestLabel(obs.test), obs.observation, obs.inference]),
        },
      },
    };

    observations.forEach((observation) => {
      const outcome = testResults.find((result) => result.test === observation.test);
      onMeasurementsUpdate?.({
        name: `${gasTestLabel(observation.test)}: ${observation.observation}`,
        value: outcome?.result === 'positive' ? 1 : outcome?.result === 'warning' ? 0.5 : 0,
        unit: outcome?.result || 'observation',
      });
    });

    if (onCalculationFeedback) {
      onCalculationFeedback(feedback);
    }

    setShowResult(true);
  };

  const resetExperiment = () => {
    setCurrentGas(randomGasId());
    setObservations([]);
    setTestResults([]);
    setIdentifiedGas('');
    setReasoning('');
    setSafetyMessages([]);
    setShowResult(false);
  };

  const activeGas = currentGas ? getGas(currentGas) : null;
  const reaction = currentGas ? getReaction(currentGas) : null;
  const collection = currentGas ? determineCollectionMethod(currentGas) : null;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ color: '#176b57', fontWeight: 'bold' }}>
        Test for Gases
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Test the unknown sample, record observations and inferences, then identify the gas.
      </Typography>
      <PracticalInstructions
        title="Tests for gases: step-by-step practical"
        introduction="Use a fresh small sample for each test. Choose tests that distinguish the unknown gas, record what you observe, and make the inference from the evidence. The sample identity is revealed after submission."
        sections={[
          { title: '1. Set up and collect a gas sample', steps: [
            'Read the gas-generation reaction, reactants, conditions, and apparatus for the preparation assigned by your teacher. Use a delivery tube and clean, dry gas jar where appropriate.',
            'Choose collection by solubility and density: insoluble or slightly soluble gases can be collected over water; soluble gases require delivery selected by relative density.',
            'Where the preparation requires it, allow the first portion of gas to escape before collecting a sample so air in the apparatus does not contaminate the test.',
            'Keep gas samples separate and use a fresh test portion for each qualitative test.'
          ] },
          { title: '2. Select a test and record observations', steps: [
            'Select a diagnostic test button. When the observation appears, enter it in the table and add your inference in the same row.',
            'Hydrogen: bring a lighted splint to a small sample; a squeaky pop is characteristic. Never ignite a sealed sample.',
            'Oxygen: insert a glowing splint; it relights or burns more brightly.',
            'Carbon dioxide: bubble the gas through limewater; it turns milky. A flame is extinguished.',
            'Ammonia: expose damp red litmus; it turns blue. Hydrogen chloride vapour produces dense white fumes of ammonium chloride.',
            'Chlorine: damp litmus is bleached. This toxic gas requires a fume cupboard in a real laboratory.',
            'Sulfur dioxide: acidified dichromate paper changes from orange to green; damp blue litmus can turn red.',
            'Hydrogen chloride: damp blue litmus turns red; ammonia produces dense white fumes.',
            'Nitrogen dioxide is brown and acidic; damp blue litmus turns red. Do not deliberately smell it.',
            'Hydrogen sulfide blackens lead acetate paper. It is highly toxic; never use smell as a test.',
            'Nitrogen extinguishes a lighted or glowing splint and has no characteristic positive test in this set.'
          ] },
          { title: '3. Identify the gas and complete the record', steps: [
            'Compare the complete pattern of positive and negative observations; do not identify a gas from one ambiguous result alone.',
            'Select the gas you infer and explain which observations support it or rule out alternatives.',
            'Submit results to reveal the unknown, then compare its generation reaction and recommended collection method with your observations.',
            'Use New Sample to repeat. Record the test, observation, inference, and any safety concern for every trial.'
          ] }
        ]}
        safety={[
          'Use a fume cupboard and eye protection for chlorine, sulfur dioxide, nitrogen dioxide, and hydrogen sulfide.',
          'Never inhale gases directly. Do not use smell to test highly toxic gases.',
          'Keep flames away from flammable gases except for the controlled small-sample hydrogen test, with the tube pointed away from people.',
          'Do not heat a sealed test tube or block a delivery tube while gas is being generated.'
        ]}
      />

      {safetyMessages.length > 0 && (
        <Alert severity={safetyMessages.some((message) => /fume cupboard|toxic|corrosive|sealed/i.test(message)) ? 'warning' : 'info'} sx={{ mb: 2 }}>
          {safetyMessages.join(' ')}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Virtual Lab Setup */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Virtual Laboratory" />
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                {/* Test Tube */}
                <Paper
                  elevation={3}
                  sx={{
                    width: 80,
                    height: 200,
                    borderRadius: '0 0 20px 20px',
                    background: `linear-gradient(to bottom, rgba(227,242,253,0.45) 0%, ${currentGas ? GAS_APPEARANCE[currentGas] : 'rgba(187,222,251,0.5)'} 100%)`,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    position: 'relative',
                  }}
                  aria-label="Unknown gas sample"
                >
                  <Typography variant="caption" sx={{ position: 'absolute', top: 10, fontSize: '0.7rem', color: '#000' }}>
                    {activeGas ? 'Unknown gas sample' : 'Preparing sample...'}
                  </Typography>
                  {animatingTest && (
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 20,
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: currentGas ? GAS_APPEARANCE[currentGas] : '#ccc',
                        animation: 'bubble 1s ease-in-out infinite',
                        '@keyframes bubble': {
                          '0%': { transform: 'scale(0)', opacity: 1 },
                          '50%': { transform: 'scale(1)', opacity: 0.7 },
                          '100%': { transform: 'scale(0)', opacity: 0 },
                        },
                      }}
                    />
                  )}
                </Paper>

                {/* Test Tools */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(88px, 1fr))', gap: 1, width: '100%' }}>
                  {TEST_TOOLS.map((item) => (
                    <Box key={item.key} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                      <Tooltip title={`${item.label} Test`}>
                        <Fab
                          size="small"
                          aria-label={`${item.label} test`}
                          color={item.key === 'lit-splint' ? 'error' : item.key === 'glowing-splint' ? 'warning' : 'primary'}
                          onClick={() => performTest(item.key)}
                          disabled={!currentGas || animatingTest !== '' || testResults.some((result) => result.test === item.key)}
                        >
                          {item.icon}
                        </Fab>
                      </Tooltip>
                      <Typography variant="caption" sx={{ color: '#000', fontWeight: 600 }}>
                        {item.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Observation Table */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Observation Table" />
            <CardContent>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Test Performed</TableCell>
                      <TableCell>Observation</TableCell>
                      <TableCell>Inference</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {observations.map((obs) => (
                      <TableRow key={obs.id}>
                        <TableCell>{gasTestLabel(obs.test)}</TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            value={obs.observation}
                            onChange={(e) => updateObservation(obs.id, 'observation', e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            value={obs.inference}
                            onChange={(e) => updateObservation(obs.id, 'inference', e.target.value)}
                            placeholder="What does this indicate?"
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => deleteObservation(obs.id)}>
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Gas Identification */}
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Gas Identification" />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Identified Gas"
                    value={identifiedGas}
                    onChange={(e) => setIdentifiedGas(e.target.value)}
                    SelectProps={{ native: true }}
                  >
                    <option value="">Select gas...</option>
                    {(listGasIds() as GasId[]).map((gasId) => {
                      const gas = getGas(gasId);
                      return <option key={gas.id} value={gas.id}>{gas.name} ({gas.formula})</option>;
                    })}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Reasoning"
                    value={reasoning}
                    onChange={(e) => setReasoning(e.target.value)}
                    placeholder="Explain your identification based on the tests performed..."
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Controls */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button variant="contained" color="primary" onClick={submitResults} disabled={!identifiedGas || testResults.length === 0 || animatingTest !== ''}>
              Submit Results
            </Button>
            <Button variant="outlined" onClick={resetExperiment}>
              New Sample
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Result Dialog */}
      <Dialog open={showResult} onClose={() => setShowResult(false)} maxWidth="md" fullWidth>
        <DialogTitle>Experiment Results</DialogTitle>
        <DialogContent>
          <Alert severity={identifiedGas === currentGas ? 'success' : 'warning'} sx={{ mb: 2 }}>
            The sample was <strong>{activeGas?.name} ({activeGas?.formula})</strong>. Your identification was {identifiedGas === currentGas ? 'correct' : 'not correct'}.
          </Alert>
          <Typography variant="h6" gutterBottom>Test Results:</Typography>
          {testResults.map((result) => (
            <Chip
              key={result.test}
              label={`${gasTestLabel(result.test)}: ${result.result} - ${result.observation}`}
              color={result.result === 'positive' ? 'success' : result.result === 'warning' ? 'warning' : 'default'}
              sx={{ m: 0.5 }}
            />
          ))}
          {reaction && collection && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight={700}>Preparation and collection</Typography>
              <Typography variant="body2">{reaction.equation}</Typography>
              <Typography variant="body2" color="text.secondary">Reactants: {reaction.reactants.join(' + ')}. Conditions: {reaction.conditions.join(', ')}.</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>Collection: {collection.method.replaceAll('-', ' ')}. {collection.reason}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Safety: {activeGas?.toxicity}; {activeGas?.flammability}. {reaction.notes}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowResult(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GasTestSimulation;